/**
 * TerminalsShell — Plan E E.5+E.6 + Pivot C + Workstream Y.1a
 *
 * Y.1a (2026-05-03) replaces the static 5-pane TTYD_SERVICES list with a
 * dynamic grid driven by the backend pane_manager. Each pane is a logical
 * (agent_type, NATO-letter) pair tracked server-side; the SPA renders one
 * TerminalTile per registered pane.
 *
 * URL construction stays Pivot C (same-origin) but adds `?instance={name}`
 * so the backend ttyd_proxy can correlate frames with the registered pane
 * (touch last_activity, observe close-events). Omitting the query keeps
 * legacy single-pane behavior, but Y.1a always passes a name.
 *
 * On mount we re-fetch the canonical list from the backend; localStorage
 * cache provides instant first-render before the API responds.
 *
 * Pre-Y.1a behavior preserved by removing the static TTYD_SERVICES list
 * here — the 5 agent types are now in the paneStore + pane_manager.
 */
import { useEffect, useState } from 'react';
import { TerminalGrid, type TerminalEntry } from './TerminalGrid';
import { HermesDashboardTile } from './HermesDashboardTile';
import { ResizableVoicePanel } from '../voice/ResizableVoicePanel';
import { useTenantStore } from '@/stores/tenantStore';
import { usePaneStore, ALLOWED_AGENT_TYPES, type AgentType } from '@/stores/paneStore';
import { useUIStore } from '@/stores/uiStore';
import { useSpaContextStore } from '@/stores/spaContextStore';
import { buildTerminalsSnapshot } from './terminalsSnapshot';

const AGENT_LABELS: Record<AgentType, string> = {
  openclaw_shell: 'OpenClaw',
  opencode_shell: 'OpenCode',
  hermes_agent: 'Hermes Agent',
  // Y.1b — shell = raw bash in the openclaw container's ttyd (no auto-launch).
  // 1.212.0 (R3 LIVE 2026-05-27): renamed 'Shell' → 'Bash' for clarity. The
  // spawn attaches to the openclaw container's user shell (openclaw@<tenant>:/app$);
  // calling it 'Shell' was ambiguous next to OpenClaw/OpenCode/Goose entries
  // that ALSO open a shell-like ttyd. 'Bash' makes it explicit this is the
  // raw shell (no auto-launched agent harness).
  shell: 'Bash',
  // Plan E (2026-05-08) — Pi resolves to t440-pi-shell-{tenant}.
  pi_shell: 'Pi',
};

function paneToEntry(
  pane: { name: string; agent_type: AgentType; display_name: string | null },
  killFn: (name: string) => void,
  renameFn: (name: string, displayName: string | null) => void,
): TerminalEntry {
  const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const url = `${wsScheme}//${host}/api/ttyd-proxy/${pane.agent_type}?instance=${encodeURIComponent(pane.name)}`;
  // Display label = display_name overlay if set; otherwise NATO name (e.g.
  // "OpenClaw alpha") so users can verbally reference the pane (Workstream Z
  // prereq). Format: "{Agent} {nato}" auto, or user's free-form override.
  // `!== null` (not truthy) — `display_name` type is `string | null`. A stale
  // cache entry holding `""` (e.g. pre-Y.3 serialized cache) would silently
  // fall back to NATO under a truthy check; explicit null-check is safer
  // and matches Z dispatcher semantics (Y.3 reviewer C4).
  let label: string;
  if (pane.display_name !== null) {
    label = pane.display_name;
  } else {
    const [, nato] = pane.name.split('-', 2);
    // A cached or server-returned pane can carry a type this SPA no longer
    // offers (goose_shell, deprecated 2026-10-21) — show the type's own name.
    const agentLabel: string | undefined = AGENT_LABELS[pane.agent_type];
    label = `${agentLabel ?? pane.agent_type} ${nato ?? ''}`.trim();
  }
  return {
    url,
    label,
    onClose: () => killFn(pane.name),
    onRename: (newName) => renameFn(pane.name, newName),
  };
}

function SpawnControls() {
  const { spawn, panes, error, limits } = usePaneStore();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Y.2 — fall back to ttyd's hard 10-cap if /limits hasn't responded yet.
  const perTypeCap = limits?.max_panes_per_agent_type ?? 10;
  const tenantCap = limits?.max_panes_per_tenant ?? 50;
  const tenantAtCap = panes.length >= tenantCap;

  const handleSpawn = async (agentType: AgentType) => {
    setOpen(false);
    setBusy(true);
    try {
      await spawn(agentType);
    } catch {
      // error surfaced via paneStore.error; don't crash the UI
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-vibe-border text-xs">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={busy || tenantAtCap}
          data-testid="spawn-pane-button"
          title={tenantAtCap ? `Tenant cap reached (${panes.length}/${tenantCap})` : undefined}
          className="px-3 py-1 rounded bg-vibe-accent text-white hover:bg-vibe-accent-hover disabled:opacity-50"
        >
          + Spawn ▾
        </button>
        {open && (
          <div
            className="absolute left-0 top-full mt-1 w-56 z-10 rounded border border-vibe-border bg-vibe-bg-secondary shadow-lg"
            data-testid="spawn-pane-menu"
          >
            {ALLOWED_AGENT_TYPES.map((t) => {
              const used = panes.filter((p) => p.agent_type === t).length;
              const typeAtCap = used >= perTypeCap;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleSpawn(t)}
                  disabled={typeAtCap}
                  data-testid={`spawn-pane-option-${t}`}
                  title={typeAtCap ? `${AGENT_LABELS[t]} cap reached (${used}/${perTypeCap})` : undefined}
                  className="w-full text-left px-3 py-2 text-vibe-text hover:bg-vibe-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed flex justify-between"
                >
                  <span>{AGENT_LABELS[t]}</span>
                  <span className="text-vibe-text-secondary">
                    {used}/{perTypeCap}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <span className="text-vibe-text-secondary">
        {panes.length}/{tenantCap} pane{panes.length === 1 ? '' : 's'}
      </span>
      {error && <span className="text-vibe-error ml-2">{error}</span>}
    </div>
  );
}

export function TerminalsShell() {
  const tenantId = useTenantStore((s) => s.tenantId);
  const { panes, refresh, refreshLimits, kill, rename } = usePaneStore();

  useEffect(() => {
    if (tenantId) {
      void refresh();
      void refreshLimits();
    }
  }, [tenantId, refresh, refreshLimits]);

  // 2026-07-04 — the Terminals page shows either the ttyd terminal grid OR the
  // embedded hermes-dashboard web GUI ("the GUI version of the hermes agent").
  const [view, setView] = useState<'terminals' | 'hermes'>('terminals');

  // Publish an on-screen snapshot so the voice ("BRIDGE") and Pi agents can SEE
  // which agent terminals are open. GATED on the Terminals view being active
  // (all shells stay mounted under display:none; only the visible one owns the
  // shared snapshot). (See ghl/App.tsx.) INTERACT (spawn/kill) stays in the
  // Spawn/close controls — those are stateful and not put on the bus here.
  const viewMode = useUIStore((s) => s.viewMode);
  const setSpaContext = useSpaContextStore((s) => s.setSpaContext);
  useEffect(() => {
    if (viewMode !== 'terminals') return;
    setSpaContext(buildTerminalsSnapshot(panes));
  }, [viewMode, panes, setSpaContext]);

  // 2026-07-04 — INTERACT: let the agents flip Terminals ↔ Hermes GUI on command
  // (vibe:module-action set_terminals_view {view}). Benign local UI. Mirrors the
  // KnowledgeShell set_nav listener; gated on the Terminals view being active.
  useEffect(() => {
    const onModuleAction = (e: Event) => {
      const d = (e as CustomEvent).detail as
        | { action?: string; params?: Record<string, unknown> }
        | undefined;
      if (viewMode !== 'terminals' || d?.action !== 'set_terminals_view') return;
      const next = d.params?.view;
      if (next === 'terminals' || next === 'hermes') setView(next);
    };
    window.addEventListener('vibe:module-action', onModuleAction);
    return () => window.removeEventListener('vibe:module-action', onModuleAction);
  }, [viewMode]);

  if (!tenantId) {
    return (
      <div className="flex flex-col h-full w-full overflow-hidden bg-vibe-bg text-vibe-text">
        {/* TopBar hoisted to App level (2026-07-03) — this early-return branch
            no longer renders its own chrome. */}
        <div className="flex-1 flex items-center justify-center">
          <p className="text-vibe-text-secondary text-sm">
            no tenant identified — /api/me has not yet returned a tenant_id
          </p>
        </div>
      </div>
    );
  }

  const entries: TerminalEntry[] = panes
    .filter((p) => p.state !== 'closed')
    .map((p) =>
      paneToEntry(
        { name: p.name, agent_type: p.agent_type, display_name: p.display_name },
        (n) => void kill(n),
        (n, dn) => void rename(n, dn),
      ),
    );

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-vibe-bg text-vibe-text">
      {/* TopBar hoisted to App level (2026-07-03) — voice strip stays here. */}
      <ResizableVoicePanel />
      {/* 2026-07-04 — Terminals ↔ Hermes GUI toggle. aria-label is deliberately
          NOT "View mode" (that group belongs to the App-level TopBar). */}
      <div
        className="flex items-center gap-1 px-3 py-1.5 border-b border-vibe-border bg-vibe-bg-secondary"
        role="group"
        aria-label="Terminals view"
      >
        <button
          type="button"
          onClick={() => setView('terminals')}
          aria-pressed={view === 'terminals'}
          data-testid="terminals-view-terminals"
          className={`px-2.5 py-1 rounded text-xs ${
            view === 'terminals'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary'
          }`}
        >
          Terminals
        </button>
        <button
          type="button"
          onClick={() => setView('hermes')}
          aria-pressed={view === 'hermes'}
          data-testid="terminals-view-hermes"
          className={`px-2.5 py-1 rounded text-xs ${
            view === 'hermes'
              ? 'bg-vibe-accent text-white'
              : 'text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary'
          }`}
        >
          Hermes GUI
        </button>
      </div>
      {view === 'hermes' ? (
        <div className="flex-1 overflow-hidden" data-testid="terminals-hermes-pane">
          <HermesDashboardTile />
        </div>
      ) : (
        <>
          <SpawnControls />
          <div className="flex-1 overflow-hidden p-2">
            {entries.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-vibe-text-secondary text-sm">
                  No panes yet — click <span className="font-mono">+ Spawn</span> to launch one.
                </p>
              </div>
            ) : (
              <TerminalGrid terminals={entries} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
